import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState(token ? "pending" : "error"); // pending | ok | error
  const [email, setEmail] = useState(null);
  const [err, setErr] = useState(token ? null : "Kein Bestätigungs-Code in der URL.");

  useEffect(() => {
    let active = true;
    if (!token) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/verify-email`, { token });
        if (!active) return;
        setEmail(res.data.email);
        setState("ok");
      } catch (e) {
        if (!active) return;
        setState("error");
        setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Link ungültig oder abgelaufen.");
      }
    })();
    return () => { active = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center" data-testid="verify-email-card">
        <Link to="/" className="text-[12px] uppercase tracking-[0.32em] font-medium text-sand-500 mb-10 block">
          ← Dickies-Campus
        </Link>

        {state === "pending" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-sand-500 animate-spin" strokeWidth={1.5} />
            <h1 className="font-display text-sand-800 text-3xl font-semibold mt-6">E-Mail wird bestätigt …</h1>
            <p className="mt-3 text-sand-600 text-[14.5px]">Einen Moment bitte.</p>
          </>
        )}

        {state === "ok" && (
          <>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-terra/10 text-terra">
              <CheckCircle2 className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <h1 className="font-display text-sand-800 text-3xl font-semibold mt-6" data-testid="verify-email-success">
              E-Mail bestätigt.
            </h1>
            <p className="mt-3 text-sand-600 text-[14.5px]">
              Dein Konto <strong className="text-sand-800">{email}</strong> ist freigeschaltet.<br />
              Du kannst jetzt loslegen.
            </p>
            <Link
              to="/campus/login"
              data-testid="verify-email-continue"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-12 px-6 text-[14.5px] font-medium"
            >
              Weiter zum Dickies-Campus →
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-cream-200 text-terra">
              <AlertCircle className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <h1 className="font-display text-sand-800 text-3xl font-semibold mt-6" data-testid="verify-email-error">
              Link ungültig.
            </h1>
            <p className="mt-3 text-sand-600 text-[14.5px]">{err}</p>
            <Link
              to="/campus/login"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-12 px-6 text-[14.5px] font-medium"
            >
              Zur Anmeldung →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
